import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { AmbientMesh } from '@/components/AmbientMesh';
import { Wordmark } from '@/components/Mark';
import { Surface } from '@/components/ui';

/**
 * Terms of Service — the linked-from-signup legal page. Plain English,
 * scoped to what Poof actually does today: a link-in-bio + analytics
 * service. No phantom features we don't offer.
 *
 * Customer-facing copy describes outcomes, not mechanisms — we don't
 * name "IAB escape" or other internal technique terms.
 *
 * This is the founder's initial draft. Get counsel to review before
 * relying on it for an actual dispute — jurisdiction-specific rules
 * (consumer protection, EU/UK consumer rights, auto-renewal disclosures)
 * are NOT exhaustively covered here.
 */
const LAST_UPDATED = '2026-05-19';

export default function Terms() {
  return (
    <LegalShell title="Terms of Service" lastUpdated={LAST_UPDATED}>
      <Section heading="1. The service">
        <p>
          Poof (the &ldquo;Service&rdquo;) is a link-in-bio and analytics
          product operated at poof.bio. The Service lets you publish a
          short URL, route visitors based on rules you define, deliver
          your links so they open reliably for the visitor, and forward
          conversion events to your own Meta Conversions API pixel.
        </p>
        <p>
          By creating an account or using the Service you agree to these
          Terms. If you don&rsquo;t agree, don&rsquo;t use the Service.
        </p>
      </Section>

      <Section heading="2. Your account">
        <p>
          You must be at least 18 years old (or the age of majority in
          your jurisdiction) to create an account. You are responsible
          for the security of your password and for anything done
          through your account. Tell us at{' '}
          <a href="mailto:hello@poof.bio" className="underline">
            hello@poof.bio
          </a>{' '}
          if you suspect unauthorized access.
        </p>
      </Section>

      <Section heading="3. Acceptable use">
        <p>You agree not to use the Service to:</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>Distribute malware, phishing pages, or content that violates third-party rights;</li>
          <li>Target minors with adult content, or bypass the per-link 18+ gate where it&rsquo;s required;</li>
          <li>Evade lawful platform enforcement, sanctions, or court orders;</li>
          <li>Send conversion events to Meta&rsquo;s CAPI without the consent your jurisdiction requires;</li>
          <li>Reverse-engineer, scrape at industrial scale, or attempt to disrupt the Service;</li>
          <li>Mask the destination URL in a way that materially deceives the visitor.</li>
        </ul>
        <p>
          We may suspend or terminate accounts that violate this section,
          and we may comply with takedown requests from law enforcement
          and rights holders.
        </p>
      </Section>

      <Section heading="4. Your content">
        <p>
          You retain ownership of the destination URLs, copy, images,
          and page content you publish through the Service. You grant us
          a non-exclusive, worldwide license to host, transmit, and
          display that content as needed to operate the Service.
        </p>
        <p>
          You are responsible for having the rights to anything you
          upload or link to, and for complying with the rules of the
          platforms you advertise on (Meta, TikTok, etc.).
        </p>
      </Section>

      <Section heading="5. Plans, billing, and free tier">
        <p>
          Free accounts get the core Service at no cost. Paid plans, if
          you subscribe to one, renew automatically at the interval shown
          at checkout until you cancel. You can cancel at any time from
          your account settings; cancellation takes effect at the end of
          the current billing period. Fees already paid are not
          refundable except where required by law.
        </p>
        <p>
          We may change pricing or plan features with at least 30
          days&rsquo; notice via email or in-product banner. Continued
          use after a price change is acceptance of the new price.
        </p>
      </Section>

      <Section heading="6. Third parties and platforms">
        <p>
          The Service interoperates with third parties &mdash; Meta&rsquo;s
          Conversions API, Cloudflare for delivery, your own custom
          domain&rsquo;s DNS provider, and the OS browser on the visitor&rsquo;s
          device. Their terms govern their parts of the flow. We are not
          responsible for outages, policy changes, or account actions
          taken by those third parties.
        </p>
      </Section>

      <Section heading="7. Disclaimer and liability">
        <p>
          The Service is provided &ldquo;as is.&rdquo; To the maximum
          extent permitted by law, we disclaim implied warranties of
          merchantability, fitness for a particular purpose, and
          non-infringement.
        </p>
        <p>
          To the maximum extent permitted by law, our aggregate
          liability for any claim arising out of or relating to the
          Service is limited to the greater of (a) the fees you paid us
          in the twelve months before the event giving rise to the
          claim, or (b) USD $100. Nothing in these Terms excludes
          liability that cannot be excluded by law (including consumer
          protection rights in your jurisdiction).
        </p>
      </Section>

      <Section heading="8. Termination">
        <p>
          You may close your account at any time. We may suspend or
          terminate your access for material breach of these Terms,
          legal compulsion, or to protect the Service or its users.
          Sections that by their nature should survive termination
          (ownership, disclaimers, liability, dispute terms) survive.
        </p>
      </Section>

      <Section heading="9. Changes">
        <p>
          We may update these Terms from time to time. Material changes
          will be flagged in-product or by email. The &ldquo;Last
          updated&rdquo; date at the top of this page reflects the
          version currently in force.
        </p>
      </Section>

      <Section heading="10. Contact">
        <p>
          Questions about these Terms? Email{' '}
          <a href="mailto:hello@poof.bio" className="underline">
            hello@poof.bio
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Local presentational helpers. Kept in this file (vs. a shared component)
// because they're only used by Terms + Privacy, and Privacy imports the
// shell directly from here would just hide a one-line export. Privacy has
// its own copy of these helpers for the same reason.
// ---------------------------------------------------------------------------

function LegalShell({
  title,
  lastUpdated,
  children,
}: {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}) {
  return (
    <div className="relative min-h-screen">
      <AmbientMesh />

      <div className="relative z-10 mx-auto max-w-3xl px-6 py-10 md:py-16">
        <header className="mb-10 flex items-center justify-between">
          <Wordmark to="/" size="md" />
          <RouterLink
            to="/login"
            className="text-sm text-[var(--color-on-surface-variant)] transition hover:text-[var(--color-foreground)]"
          >
            Sign in
          </RouterLink>
        </header>

        <Surface tone="raised" className="p-8 md:p-12">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
            {title}
          </h1>
          <p className="mt-2 text-sm text-[var(--color-on-surface-variant)]">
            Last updated {lastUpdated}
          </p>
          <div className="mt-8 space-y-8 text-[var(--color-foreground)]">
            {children}
          </div>
        </Surface>

        <footer className="mt-10 flex flex-wrap items-center justify-between gap-4 text-xs text-[var(--color-on-surface-variant)] opacity-70">
          <span>© 2026 Poof</span>
          <div className="flex gap-5">
            <RouterLink to="/terms" className="hover:text-[var(--color-foreground)]">
              Terms
            </RouterLink>
            <RouterLink to="/privacy" className="hover:text-[var(--color-foreground)]">
              Privacy
            </RouterLink>
            <a href="mailto:hello@poof.bio" className="hover:text-[var(--color-foreground)]">
              hello@poof.bio
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold tracking-tight">{heading}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-[var(--color-on-surface-variant)]">
        {children}
      </div>
    </section>
  );
}
